import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE, page, cardScroll, inp, lbl, btn, btnDisabled, ConfirmDialog } from './AdminUsers';
import TopBanner from '../components/TopBanner';
import AdminSidebar from '../components/AdminSidebar';

// Frontend for POST /api/admin/app-provisioning (backend/src/routes/
// adminAppProvisioning.js) — automates Section 1 of
// NEW-APP-INTEGRATION.md (creating a new connected app's OAuth2
// Provider + Application in Authentik, plus an optional access-
// restricting group) instead of clicking through Authentik's own UI.
// See integration-kit/README.md for what to do with the result — this
// page only covers the admin-side provisioning step, not the new app's
// own backend/frontend integration (that's still copied code, on
// purpose — see that README for why).

const hint = { margin: '4px 0 0', fontSize: '12px', color: '#888', lineHeight: 1.5 };

const EMPTY_FORM = { appName: '', appSlug: '', domain: '', groupName: '', logoutUri: '' };

function CopyableField({ label, value }) {
  const [copied, setCopied] = useState(false);
  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // clipboard API can fail (permissions, non-secure context) — the
      // value is still visible for manual copy either way.
    }
  }
  return (
    <div style={{ marginBottom: '12px' }}>
      <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '600', color: '#5F5E5A' }}>{label}</p>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <code style={{
          flex: 1, padding: '8px 12px', background: '#F7F6F2', border: '1px solid #eee',
          borderRadius: '6px', fontSize: '13px', color: '#1a1a1a', wordBreak: 'break-all',
        }}>
          {value}
        </code>
        <button
          type="button"
          onClick={handleCopy}
          style={{
            flexShrink: 0, padding: '8px 12px', background: copied ? '#0F6E56' : '#EDEBE4',
            color: copied ? '#fff' : '#5F5E5A', border: 'none', borderRadius: '6px',
            fontSize: '12px', fontWeight: '600', cursor: 'pointer',
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

export default function AdminAppProvisioning() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const [deprovisionSlug, setDeprovisionSlug] = useState('');
  const [deprovisionDeleteGroup, setDeprovisionDeleteGroup] = useState(false);
  const [deprovisioning, setDeprovisioning] = useState(false);
  const [deprovisionError, setDeprovisionError] = useState(null);
  const [deprovisionResult, setDeprovisionResult] = useState(null);
  const [dialog, setDialog] = useState(null);

  // Guard: only bescom-super-admins — same as bulkUsers.js's own backend
  // gate, and the same "two cases, not one" pattern every other
  // super-admin-only page here uses.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
    const groups = user.profile?.groups || [];
    if (!groups.includes('bescom-super-admins')) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setResult(null);
    setSubmitting(true);
    try {
      const body = {
        appName: form.appName.trim(),
        appSlug: form.appSlug.trim(),
        domain: form.domain.trim(),
      };
      if (form.groupName.trim()) body.groupName = form.groupName.trim();
      if (form.logoutUri.trim()) body.logoutUri = form.logoutUri.trim();

      const res = await fetch(`${API_BASE}/admin/app-provisioning`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not provision the app.');
        return;
      }
      setResult(data);
      setForm(EMPTY_FORM);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setSubmitting(false);
    }
  }

  function handleDeprovisionClick(e) {
    e.preventDefault();
    const slug = deprovisionSlug.trim();
    if (!slug) return;
    setDialog({
      title: 'Deprovision app',
      message: deprovisionDeleteGroup
        ? `Permanently delete "${slug}"'s Provider, Application, and its bound group from Authentik? This cannot be undone. The new app itself keeps running, but its SSO login and logout will stop working immediately.`
        : `Permanently delete "${slug}"'s Provider and Application from Authentik? Its bound group (if any) is left in place. This cannot be undone — the new app itself keeps running, but its SSO login and logout will stop working immediately.`,
      confirmLabel: 'Deprovision',
      danger: true,
      onConfirm: async () => {
        setDialog(null);
        setDeprovisionError(null);
        setDeprovisionResult(null);
        setDeprovisioning(true);
        try {
          const res = await fetch(
            `${API_BASE}/admin/app-provisioning/${encodeURIComponent(slug)}?deleteGroup=${deprovisionDeleteGroup}`,
            { method: 'DELETE', credentials: 'include' }
          );
          const data = await res.json();
          if (!res.ok) {
            setDeprovisionError(data.error || 'Could not deprovision the app.');
            return;
          }
          setDeprovisionResult(data);
          setDeprovisionSlug('');
          setDeprovisionDeleteGroup(false);
        } catch (e) {
          setDeprovisionError('Could not reach the identity service.');
        } finally {
          setDeprovisioning(false);
        }
      },
    });
  }

  if (authLoading || !user) return null;

  return (
    <>
      <TopBanner />
      <div className="admin-page-wrap" style={page}>
        <div className="admin-row" style={{ display: 'flex', gap: '24px', width: '100%', height: '100%' }}>
          <AdminSidebar active="app-provisioning" />
          <div style={cardScroll}>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1a1a1a' }}>App Provisioning</h2>
            <p style={{ margin: '4px 0 20px', fontSize: '13px', color: '#5F5E5A' }}>
              Creates a new connected app's OAuth2 Provider and Application in Authentik in one step — replaces
              manually clicking through Authentik's own admin UI. See <code>integration-kit/README.md</code> in the
              repo for what to do with the result afterward.
            </p>

            {error && (
              <div style={{ marginBottom: '16px', padding: '12px 16px', background: '#FDECEC', color: '#B3261E', borderRadius: '8px', fontSize: '13px' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ marginBottom: '28px', padding: '20px', border: '1px solid #eee', borderRadius: '10px', background: '#fff', maxWidth: '640px' }}>
              <div style={{ marginBottom: '18px' }}>
                <label style={lbl}>App name</label>
                <input style={inp} name="appName" value={form.appName} onChange={handleChange} placeholder="e.g. BESCOM Transfer App" required />
                <p style={hint}>Display name shown in Authentik's admin UI and any app launcher — purely cosmetic, safe to change later directly in Authentik.</p>
              </div>

              <div style={{ marginBottom: '18px' }}>
                <label style={lbl}>App slug</label>
                <input style={inp} name="appSlug" value={form.appSlug} onChange={handleChange} placeholder="e.g. bescom-transfer" required pattern="[a-z0-9]+(-[a-z0-9]+)*" title="lowercase letters, numbers, and hyphens only" />
                <p style={hint}>
                  Lowercase letters, numbers, and hyphens only. Used as the permanent identifier in the OIDC issuer/JWKS URL
                  (<code>/application/o/&lt;slug&gt;/</code>) — cannot be changed after creation without recreating the app in Authentik.
                </p>
              </div>

              <div style={{ marginBottom: '18px' }}>
                <label style={lbl}>Domain</label>
                <input style={inp} name="domain" type="url" value={form.domain} onChange={handleChange} placeholder="https://foo.bescomapps.com" required />
                <p style={hint}>
                  The new app's own live origin — must start with <code>https://</code>, no trailing slash needed.
                  The redirect URI (<code>&lt;domain&gt;/callback</code>) is derived from this automatically; register that
                  exact value in the new app's own OIDC client config.
                </p>
              </div>

              <div style={{ marginBottom: '18px' }}>
                <label style={lbl}>Access-restriction group <span style={{ fontWeight: 400, color: '#999' }}>(optional)</span></label>
                <input style={inp} name="groupName" value={form.groupName} onChange={handleChange} placeholder="e.g. bescom-transfer-users" />
                <p style={hint}>
                  If given, only members of this group will be able to launch the app — created automatically if it
                  doesn't already exist. <strong>Leave blank for no restriction</strong> (any authenticated BESCOM user can launch it) —
                  the same as every existing connected app today.
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={lbl}>Logout URI <span style={{ fontWeight: 400, color: '#999' }}>(optional)</span></label>
                <input style={inp} name="logoutUri" value={form.logoutUri} onChange={handleChange} placeholder="https://foo.bescomapps.com/api/auth/backchannel-logout" />
                <p style={hint}>
                  Where Authentik pushes an instant logout notification. Leave blank if the new app isn't deployed yet —
                  you can set this later directly in Authentik once it is. Two options once the app's backend exists:
                  its own <code>/api/auth/backchannel-logout</code> receiver (see integration-kit), or the centralized,
                  no-code-required alternative: <code>https://sso.bescomapps.com/api/webhooks/backchannel-logout/&lt;app slug&gt;</code>.
                </p>
              </div>

              <button type="submit" disabled={submitting} style={submitting ? btnDisabled : btn}>
                {submitting ? 'Provisioning…' : 'Provision App'}
              </button>
            </form>

            {result && (
              <div style={{ padding: '20px', border: '1px solid #D3E3ED', borderRadius: '10px', background: '#EFF7FC', maxWidth: '640px' }}>
                <h3 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: '700', color: '#0F6E56' }}>✅ Provisioned successfully</h3>
                <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#5F5E5A' }}>
                  Hand these values to whoever is setting up the new app's own backend/frontend
                  (see <code>integration-kit/README.md</code> for what each one is used for).
                </p>
                <CopyableField label="Client ID" value={result.clientId} />
                <CopyableField label="Redirect URI" value={result.redirectUri} />
                <CopyableField label="Application slug" value={result.applicationSlug} />
                {!result.logoutUriConfigured && (
                  <p style={{ ...hint, marginTop: '8px' }}>
                    No Logout URI was set — remember to configure one in Authentik (or via a future provisioning
                    call) once the new app is deployed, or incoming logout notifications will silently never fire.
                  </p>
                )}
                {result.group && (
                  <p style={{ ...hint, marginTop: '8px' }}>
                    Group <code>{result.group.name}</code> {result.group.created ? 'was created and' : 'was found and'} bound
                    to this app — only its members can launch it.
                  </p>
                )}
              </div>
            )}

            <h2 style={{ margin: '32px 0 4px', fontSize: '18px', fontWeight: '700', color: '#1a1a1a' }}>Deprovision an app</h2>
            <p style={{ margin: '4px 0 20px', fontSize: '13px', color: '#5F5E5A' }}>
              Removes a connected app's Provider and Application from Authentik — mainly for cleaning up a test app,
              or fully retiring one that's being decommissioned. Its SSO login/logout stop working immediately; the
              app itself is untouched (this doesn't take it down or delete any of its own data).
            </p>

            {deprovisionError && (
              <div style={{ marginBottom: '16px', padding: '12px 16px', background: '#FDECEC', color: '#B3261E', borderRadius: '8px', fontSize: '13px' }}>
                {deprovisionError}
              </div>
            )}

            {deprovisionResult && (
              <div style={{ marginBottom: '16px', padding: '12px 16px', background: '#E6F4EF', color: '#0F6E56', borderRadius: '8px', fontSize: '13px' }}>
                ✅ Deprovisioned.{' '}
                {deprovisionResult.groupsDeleted.length > 0 && `Also deleted ${deprovisionResult.groupsDeleted.length} bound group(s).`}
                {deprovisionResult.groupsLeftInPlace.length > 0 && `${deprovisionResult.groupsLeftInPlace.length} bound group(s) left in place, untouched.`}
              </div>
            )}

            <form onSubmit={handleDeprovisionClick} style={{ padding: '20px', border: '1px solid #eee', borderRadius: '10px', background: '#fff', maxWidth: '640px' }}>
              <div style={{ marginBottom: '14px' }}>
                <label style={lbl}>App slug</label>
                <input style={inp} value={deprovisionSlug} onChange={(e) => setDeprovisionSlug(e.target.value)} placeholder="e.g. test-provisioning-kit" />
                <p style={hint}>The exact app slug to remove — the same value used to provision it.</p>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#1a1a1a', marginBottom: '18px' }}>
                <input type="checkbox" checked={deprovisionDeleteGroup} onChange={(e) => setDeprovisionDeleteGroup(e.target.checked)} />
                Also delete its bound access-restriction group
              </label>
              <p style={{ ...hint, margin: '-12px 0 18px' }}>
                Off by default — the group might be reused elsewhere or have real members by now, so it's left in
                place unless you specifically ask for it to go too.
              </p>
              <button type="submit" disabled={deprovisioning || !deprovisionSlug.trim()} style={(deprovisioning || !deprovisionSlug.trim()) ? btnDisabled : { ...btn, background: '#B3261E' }}>
                {deprovisioning ? 'Deprovisioning…' : 'Deprovision App'}
              </button>
            </form>
          </div>
        </div>
      </div>
      <ConfirmDialog dialog={dialog} onCancel={() => setDialog(null)} />
    </>
  );
}
